import type { Meal, UseCanteenMenuState_UserDataFragmentFragment } from '@/generated/gql/graphql'

import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone'
import { format } from 'date-fns/format'
import { isAfter } from 'date-fns/isAfter'
import { isValid } from 'date-fns/isValid'
import { parseISO } from 'date-fns/parseISO'

import { parseServiceDate } from '../MenuHeader/ServiceSelectionGraphql/serviceWeeks'

type UpcomingCcMenus = UseCanteenMenuState_UserDataFragmentFragment['upcomingCcMenus']

const MEALS = ['breakfast', 'lunch', 'dinner'] as const

const formatMenuDay = (date: Date, timezone?: string | null): string => {
  if (!isValid(date)) return 'Invalid date'
  return timezone ? formatInTimeZone(date, timezone, 'yyyy-MM-dd') : format(date, 'yyyy-MM-dd')
}

/**
 * Resolve the date and meal to pre-fill for the canteen menu.
 *
 * A meal is selectable when its menu is published AND its delivery time
 * has not yet passed, so a meal whose order cut-off
 * has passed but whose delivery hasn't still counts.
 *
 * Selection order:
 *  1. The requested date and meal, if that meal is still selectable.
 *  2. Otherwise the first selectable meal that day, advancing past meals whose delivery has passed.
 *  3. If every meal that day has passed, the next available meal/day.
 *
 */
export const getValidDateAndMeal = (
  dateToCheck: string,
  mealToCheck: string,
  upcomingCcMenus: UpcomingCcMenus,
  { now = new Date(), timezone }: { now?: Date; timezone?: string | null } = {},
): { date: string; meal: Meal } => {
  const parseDate = (value: string) =>
    timezone ? parseServiceDate(value, timezone) : parseISO(value)
  let dateToUse = formatMenuDay(now, timezone)
  let mealToUse: string = 'lunch'

  const mealDeliveryHasPassed = (
    mealService?: { timeslots: Array<{ endTime: string }> } | null,
  ) => {
    if (!mealService || mealService.timeslots.length === 0) return false
    return mealService.timeslots.every((timeslot) => isAfter(now, parseDate(timeslot.endTime)))
  }
  const mealIsSelectable = (
    mealService?: {
      menuIsPublished: boolean
      timeslots: Array<{ endTime: string }>
    } | null,
  ) => Boolean(mealService?.menuIsPublished) && !mealDeliveryHasPassed(mealService)

  const foundDate = upcomingCcMenus.find(
    (menu) => formatMenuDay(parseDate(menu.date), timezone) === dateToCheck,
  )
  const firstServiceWithMenu = upcomingCcMenus.find((menu) =>
    MEALS.some((meal) => mealIsSelectable(menu[meal])),
  )

  if (foundDate) {
    dateToUse = dateToCheck || dateToUse
    const requestedMeal =
      mealToCheck && foundDate[mealToCheck as Meal] ? (mealToCheck as Meal) : undefined
    const firstSelectableToday = MEALS.find((meal) => mealIsSelectable(foundDate[meal]))
    if (requestedMeal && mealIsSelectable(foundDate[requestedMeal])) {
      mealToUse = requestedMeal
    } else if (firstSelectableToday) {
      mealToUse = firstSelectableToday
    } else if (firstServiceWithMenu) {
      // Every meal today has passed so load the next available meal/day.
      dateToUse = formatMenuDay(parseDate(firstServiceWithMenu.date), timezone)
      mealToUse = MEALS.find((meal) => mealIsSelectable(firstServiceWithMenu[meal])) || mealToUse
    }
  } else if (firstServiceWithMenu) {
    dateToUse = formatMenuDay(parseDate(firstServiceWithMenu.date), timezone)
    mealToUse = MEALS.find((meal) => mealIsSelectable(firstServiceWithMenu[meal])) || mealToUse
  }

  return { date: dateToUse, meal: mealToUse as Meal }
}
